/**
 * API client for billing operations.
 * Handles subscription, payment method, invoice, plan-catalog,
 * checkout-session, portal-session, and seat-change requests.
 */

import { fetchWithDefaults } from '@/lib/http/client';

import type {
  BillingPlansResponse,
  BillingSubscription,
  CheckoutSession,
  CheckoutSessionOptions,
  FetchInvoicesOptions,
  InvoicePaginatedResponse,
  PortalSession,
  SeatChangeResponse,
} from './types';

/**
 * Fetch the current subscription for an organization.
 * Returns null if no subscription exists (404).
 */
export async function fetchSubscription(
  orgId: number
): Promise<BillingSubscription | null> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/subscription/`
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch subscription');
  }

  return response.json();
}

/**
 * Fetch paginated invoice history for an organization.
 */
export async function fetchInvoices(
  orgId: number,
  options: FetchInvoicesOptions = {}
): Promise<InvoicePaginatedResponse> {
  const { page = 1, pageSize, startDate, endDate } = options;

  const params = new URLSearchParams();
  params.set('page', String(page));
  if (pageSize) {
    params.set('page_size', String(pageSize));
  }
  if (startDate) {
    params.set('start_date', startDate);
  }
  if (endDate) {
    params.set('end_date', endDate);
  }

  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/invoices/?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch invoices');
  }

  return response.json();
}

/**
 * Create a Stripe Billing Portal session.
 * Returns a URL to redirect the user to manage their subscription.
 */
export async function createPortalSession(
  orgId: number
): Promise<PortalSession> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/portal-session/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to create portal session');
  }

  return response.json();
}

/**
 * Create a Stripe Checkout session for a new subscription.
 *
 * The backend's CheckoutSessionRequestSerializer requires either a
 * ``price_id`` or both ``tier`` and ``cadence``; an empty body is
 * rejected with 400. Callers MUST supply ``{tier, cadence, seatCount}``
 * — the backend resolves the pair via Stripe Price ``lookup_key`` so
 * the catalog can be re-priced operator-side without a deploy.
 */
export async function createCheckoutSession(
  orgId: number,
  options: CheckoutSessionOptions
): Promise<CheckoutSession> {
  const body = {
    tier: options.tier,
    cadence: options.cadence,
    seat_count: options.seatCount,
  };

  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/checkout-session/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to create checkout session');
  }

  return response.json();
}

export class CreditCheckoutUnavailableError extends Error {
  constructor() {
    super('Credit packs are not available');
    this.name = 'CreditCheckoutUnavailableError';
  }
}

/**
 * Create a Stripe Checkout session for a credit-pack purchase.
 *
 * Degrades gracefully when the endpoint is not yet deployed (404) or when no
 * credit-pack products are configured in Stripe (501) — both surface as
 * CreditCheckoutUnavailableError so the UI can show "Coming soon".
 */
export async function createCreditCheckoutSession(
  orgId: number
): Promise<CheckoutSession> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/credit-checkout-session/`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } }
  );
  if (response.status === 404 || response.status === 501) {
    throw new CreditCheckoutUnavailableError();
  }
  if (!response.ok) {
    throw new Error('Failed to create credit checkout session');
  }
  return response.json();
}

/**
 * Rebuild a Stripe Checkout session for a user who came in via a paid
 * invite code but cancelled out of their original checkout.
 *
 * The backend reads the user's redeemed invite code (with the deal package
 * baked in — payment_mode, tier, cadence, negotiated price, coupon, trial)
 * and constructs a fresh Checkout session. Idempotent under repeated
 * clicks; the invite stays USED but its config persists.
 */
export async function resumeInviteCheckout(
  orgId: number
): Promise<CheckoutSession> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/checkout-session/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ from_invite: true }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to resume invite checkout');
  }

  return response.json();
}

/**
 * Fetch the tier × cadence catalog for the plan-picker UI.
 */
export async function fetchPlans(orgId: number): Promise<BillingPlansResponse> {
  const response = await fetchWithDefaults(`/api/orgs/${orgId}/billing/plans/`);

  if (!response.ok) {
    throw new Error('Failed to fetch billing plans');
  }

  return response.json();
}

/**
 * Change the seat count on the org's active subscription.
 *
 * Backend policy: increases are charged immediately (prorated), decreases
 * apply at the next renewal. The response includes any immediately-charged
 * invoice ID + amount so the UI can confirm what was billed.
 */
export async function changeSeats(
  orgId: number,
  seatCount: number
): Promise<SeatChangeResponse> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/seats/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ seat_count: seatCount }),
    }
  );

  if (!response.ok) {
    let detail: string | null = null;
    try {
      const body = (await response.json()) as { error?: string };
      detail = body?.error ?? null;
    } catch {
      // ignore — non-JSON error body
    }
    throw new Error(detail ?? 'Failed to change seat count');
  }

  return response.json();
}

/**
 * Response shape from the cadence-change endpoint. ``invoice_id`` and
 * ``proration_cents`` are present when Stripe creates an immediate
 * proration invoice for the monthly → yearly switch (always_invoice
 * proration_behavior on the backend).
 */
export interface CadenceChangeResponse {
  billing_cadence: 'yearly';
  invoice_id: string | null;
  proration_cents: number | null;
}

/**
 * Switch the subscription from monthly to yearly billing. The reverse
 * direction (yearly → monthly) is intentionally not exposed in
 * self-serve — yearly-to-monthly downgrades are handled by sales.
 */
export async function changeCadence(
  orgId: number
): Promise<CadenceChangeResponse> {
  const response = await fetchWithDefaults(
    `/api/orgs/${orgId}/billing/cadence/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ target_cadence: 'yearly' }),
    }
  );

  if (!response.ok) {
    let detail: string | null = null;
    try {
      const body = (await response.json()) as { error?: string };
      detail = body?.error ?? null;
    } catch {
      // ignore — non-JSON error body
    }
    throw new Error(detail ?? 'Failed to switch billing cadence');
  }

  return response.json();
}
